import { useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Zap, BadgeCheck, Atom, Clock, UsersRound, Phone, LayoutGrid, Check } from "lucide-react";
import { HERO_FEATURES } from "../lib/data";

const ICONS = { BadgeCheck, Atom, Clock, UsersRound };
const ease = [0.16, 1, 0.3, 1];

const Line = ({ children, delay = 0 }) => (
  <motion.span className="block" initial={{ opacity: 0, y: 26 }} animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.85, ease, delay }}>{children}</motion.span>
);

export default function Hero() {
  const videoRef = useRef(null);
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    v.load();
    const tryPlay = () => v.play().catch(() => {});
    tryPlay();
    v.addEventListener("canplay", tryPlay);
    return () => v.removeEventListener("canplay", tryPlay);
  }, []);
  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden bg-ink pt-28 pb-16 lg:py-0" data-testid="hero-section">
      <div className="absolute inset-0 lg:left-auto lg:right-0 lg:w-[53%] z-0">
        <video ref={videoRef} autoPlay muted loop playsInline preload="auto" poster="/hero-poster.jpg" data-testid="hero-video"
          onCanPlay={(e) => e.currentTarget.play().catch(() => {})}
          className="w-full h-full object-cover">
          <source src="/hero-video.webm" type="video/webm" />
          <source src="/hero-video.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-black/45" />
        <div className="absolute inset-0 bg-ink/40 lg:hidden" />
        <div className="hidden lg:block absolute inset-0 bg-gradient-to-r from-ink via-ink/40 to-transparent" />
        <div className="hidden lg:block absolute inset-y-0 left-0 w-72 bg-gradient-to-r from-ink to-transparent" />
      </div>
      <div className="absolute inset-0 z-[1] grid-lines opacity-30 pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full">
        <div className="max-w-xl lg:max-w-[640px]">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15, duration: 0.7 }}
            className="flex items-center gap-2.5 mb-5">
            <Zap className="h-4 w-4 text-volt fill-volt" />
            <span className="text-xs md:text-sm font-bold tracking-[0.22em] uppercase text-volt">Elektrovorbereitung & Kernbohrungen</span>
          </motion.div>

          <h1 className="font-heading font-black uppercase tracking-tighter leading-[0.95] text-white text-4xl sm:text-5xl lg:text-6xl">
            <Line delay={0.3}>Präzision für jede</Line>
            <Line delay={0.45}><span className="text-volt">Elektroinstallation</span></Line>
          </h1>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.85 }} className="flex items-center gap-3 mt-6 mb-5">
            <span className="h-px w-12 bg-volt/60" />
            <Zap className="h-4 w-4 text-volt fill-volt" />
          </motion.div>

          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.7 }}
            className="max-w-md text-base md:text-lg text-white/70 leading-relaxed font-medium">
            Spezialisiert auf Elektrovorbereitung und präzise Kernbohrungen – für saubere,
            normgerechte und professionelle Ergebnisse.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.7 }}
            className="mt-9 grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-6">
            {HERO_FEATURES.map((f) => {
              const Icon = ICONS[f.icon];
              return (
                <div key={f.l1} data-testid={`hero-feature-${f.l1}`} className="group flex flex-col items-start gap-2">
                  <span className="grid place-items-center h-11 w-11 rounded-xl border border-volt/40 text-volt group-hover:bg-volt group-hover:text-black transition-all duration-300">
                    <Icon className="h-5 w-5" strokeWidth={2} />
                  </span>
                  <span className="text-[0.7rem] font-bold uppercase tracking-wider text-white/70 leading-tight">
                    {f.l1}<br />{f.l2}
                  </span>
                </div>
              );
            })}
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2, duration: 0.7 }}
            className="mt-10 flex flex-wrap gap-4">
            <a href="#kontakt" data-scroll="#kontakt" data-testid="hero-cta-anfragen"
              className="group flex items-center gap-3 rounded-full bg-volt text-black font-bold text-base md:text-lg px-10 py-5 hover:scale-105 transition-transform duration-300 shadow-[0_0_36px_rgba(245,133,26,0.35)]">
              <Phone className="h-5 w-5" strokeWidth={2.5} /> Jetzt anfragen
            </a>
            <a href="#leistungen" data-scroll="#leistungen" data-testid="hero-cta-leistungen"
              className="flex items-center gap-3 rounded-full border border-white/25 text-white font-bold text-base md:text-lg px-10 py-5 hover:border-volt hover:text-volt transition-all duration-300">
              <LayoutGrid className="h-5 w-5" strokeWidth={2.2} /> Unsere Leistungen
            </a>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4, duration: 0.7 }}
            className="mt-7 flex flex-wrap gap-x-6 gap-y-2.5" data-testid="hero-badges">
            {["Schnelle Terminvergabe", "Kostenloses Angebot", "Zuverlässige Ausführung"].map((b) => (
              <span key={b} data-testid={`hero-badge-${b}`} className="flex items-center gap-2 text-sm font-semibold text-white/80">
                <Check className="h-4 w-4 text-volt shrink-0" strokeWidth={3} /> {b}
              </span>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
